import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import Select from "react-select";
import toast from "react-hot-toast";

import SignaturePad from "../../../Component/shared/signaturePad";
import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";
import { CurrentProviderData } from "../../../helpers/constant";
import { getSessionStorage } from "../../../helpers/storage";
import {
  getMinorConsentForm,
  saveMinorConsent,
  saveParentSignatue,
  getInsuranceCompanys,
} from "./service";
import { GetProviderList } from "../Service";

import "./MinorConsent.css";

/* ─── helpers ──────────────────────────────────────────────────────────────── */
const SHARED_SIG_KEY = "sharedPatientSignature";

const toStr = (val) => (val == null ? "" : String(val).trim());

/* ═══════════════════════════════════════════════════════════════════════════
   MinorConsentForm
═══════════════════════════════════════════════════════════════════════════ */
const MinorConsentForm = forwardRef(function MinorConsentForm(props, ref) {
  const { selectedRow, copySignature, signatureMiss } = props;

  /* ── session ─────────────────────────────────────────────────────────── */
  const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
  const SessionData = JSON.parse(sessionStorage.getItem("sessionData") || "{}");
  const providerData = getSessionStorage(CurrentProviderData) || [];

  const roleName = SessionData?.Role?.Name?.trim().toLowerCase() ?? "";
  const isFrontDesk =
    roleName === "frontdesk" ||
    roleName === "front desk" ||
    roleName === "admin";

  const caseId =
    selectedRow?.searchResult?.Patient?.CaseID ??
    selectedRow?.data?.CaseID ??
    tokenData?.caseId ??
    "";
  const caseNo =
    selectedRow?.searchResult?.Patient?.CaseNo ??
    selectedRow?.data?.CaseNo ??
    "";
  const companyId =
    selectedRow?.searchResult?.Patient?.Account?.ID ??
    selectedRow?.data?.Account?.ID ??
    tokenData?.companyId ??
    "";

  /* ── state ───────────────────────────────────────────────────────────── */
  const [loading, setLoading] = useState(false);
  const [sigPadOpen, setSigPadOpen] = useState(false);
  const [providerOptions, setProviderOptions] = useState([]);
  const [insuranceList, setInsuranceList] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [patientSignImg, setPatientSignImg] = useState("");

  /* form fields */
  const [relationship, setRelationship] = useState("");
  const [familyAddress, setFamilyAddress] = useState("");
  const [fatherTelephone, setFatherTelephone] = useState("");
  const [motherTelephone, setMotherTelephone] = useState("");
  const [insuranceId, setInsuranceId] = useState(null);
  const [policy, setPolicy] = useState("");
  const [additionalContactNumber, setAdditionalContactNumber] = useState("");
  const [preferredHospital, setPreferredHospital] = useState("");
  const [relationshipToChild, setRelationshipToChild] = useState("");
  const [name, setName] = useState("");
  const [authEffectiveFrom, setAuthEffectiveFrom] = useState("");
  const [dayOf, setDayOf] = useState("");
  const [year, setYear] = useState("");
  const [providerName, setProviderName] = useState("");

  /* ── stale-closure-free ref ──────────────────────────────────────────── */
  const latestRef = useRef({});
  useEffect(() => {
    latestRef.current = {
      patientSignImg,
      relationship,
      familyAddress,
      fatherTelephone,
      motherTelephone,
      insuranceId,
      policy,
      additionalContactNumber,
      preferredHospital,
      relationshipToChild,
      name,
      authEffectiveFrom,
      dayOf,
      year,
      selectedProvider,
    };
  }, [
    patientSignImg,
    relationship,
    familyAddress,
    fatherTelephone,
    motherTelephone,
    insuranceId,
    policy,
    additionalContactNumber,
    preferredHospital,
    relationshipToChild,
    name,
    authEffectiveFrom,
    dayOf,
    year,
    selectedProvider,
  ]);

  /* ── imperative handle ───────────────────────────────────────────────── */
  useImperativeHandle(ref, () => ({
    save: () => saveFunction(),
    isPending: () => !latestRef.current.patientSignImg,
    getSignature: () => latestRef.current.patientSignImg || "",
    applySignature: (sig) => {
      if (!sig) return;
      setPatientSignImg(sig);
    },
  }));

  useEffect(() => {
    props?.onReady?.("minor_consent");
  }, [patientSignImg]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── bootstrap ───────────────────────────────────────────────────────── */
  useEffect(() => {
    callApi();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const callApi = async () => {
    setLoading(true);
    try {
      const providerRes = await GetProviderList(companyId);
      const provOpts = (providerRes || []).map((x) => ({
        label: x.Name,
        value: x.Id,
      }));
      setProviderOptions(provOpts);

      const insRes = await getInsuranceCompanys(companyId);
      const insOpts = (insRes || []).map((item) => ({
        ...item,
        label: item.Name,
        value: item.ID,
      }));
      setInsuranceList(insOpts);

      await loadFormData(provOpts, insOpts);
    } catch (err) {
      console.error("MinorConsentForm callApi error:", err);
      toast.error("Something went wrong while loading data.");
    } finally {
      setLoading(false);
    }
  };

  /* ── GET / bind form data ────────────────────────────────────────────── */
  const loadFormData = async (provOpts, insOpts) => {
    try {
      const res = await getMinorConsentForm(caseId, companyId);
      const mc = res?.Record?.MinorConsent;

      if (!mc) {
        signatureMiss?.(true);
        return;
      }

      setRelationship(toStr(mc.Relationship));
      setFamilyAddress(toStr(mc.FamilyAddress));
      setFatherTelephone(toStr(mc.FatherTelephone));
      setMotherTelephone(toStr(mc.MotherTelephone));
      setPolicy(toStr(mc.Policy));
      setAdditionalContactNumber(toStr(mc.AdditionalContactNumber));
      setPreferredHospital(toStr(mc.PreferredHospital));
      setRelationshipToChild(toStr(mc.RelationshipToChild));
      setName(toStr(mc.Name));
      setAuthEffectiveFrom(toStr(mc.AuthEffectiveFrom));
      setDayOf(toStr(mc.DayOf));
      setYear(toStr(mc.YEAR));

      const insIdStr = toStr(mc.InsuranceId);
      const foundIns = insOpts.find((x) => x.value === insIdStr) || null;
      setInsuranceId(foundIns);

      const provIdStr = toStr(mc.ProviderId);
      const foundProv =
        provOpts.find((x) => x.value === provIdStr) || provOpts[0] || null;
      setSelectedProvider(foundProv);
      const resolvedProviderName =
        foundProv?.label ||
        providerData[0]?.ProviderName ||
        tokenData?.providerName ||
        tokenData?.ProviderName ||
        "";
      setProviderName(resolvedProviderName);

      // If copySignature is ON and a shared sig exists, prefer it (same as EPAT)
      const savedSig = copySignature ? sessionStorage.getItem(SHARED_SIG_KEY) : null;
      const resolvedSig = savedSig ||
        (mc?.Signature ? `data:image/png;base64,${mc.Signature}` : "");

      setPatientSignImg(resolvedSig);

      if (!resolvedSig) signatureMiss?.(true);
    } catch (err) {
      console.error("loadFormData error:", err);
      toast.error("Failed to load Minor Consent data.");
    }
  };

  /* ── resolve provider ID for save ───────────────────────────────────── */
  const resolveProviderId = (latest) => {
    if (isFrontDesk) {
      if (providerOptions.length === 1) return providerOptions[0]?.value || "";
      return latest.selectedProvider?.value || "";
    }
    return providerData[0]?.ProviderId || tokenData?.doctorId || "";
  };

  /* ── SAVE ────────────────────────────────────────────────────────────── */
  // Unified save: form data + signature API both fire here, same as EPAT.
  // signUpdatedCall no longer calls saveParentSignatue directly.
  const saveFunction = async () => {
    const latest = latestRef.current;
    setLoading(true);
    try {
      const rawSig = latest.patientSignImg
        ? latest.patientSignImg.replace(/^data:image\/\w+;base64,/, "")
        : "";

      const payload = {
        CaseId: caseId,
        CompanyId: companyId,
        ProviderId: resolveProviderId(latest),
        SZ_Day: 0,
        SZ_Month: "",
        SZ_Year: 0,
        CityState: "",
        Relationship: latest.relationship,
        FamilyAddress: latest.familyAddress,
        FatherTelephone: latest.fatherTelephone,
        MotherTelephone: latest.motherTelephone,
        InsuranceId: latest.insuranceId?.value || "",
        Policy: latest.policy,
        AdditionalContactNumber: latest.additionalContactNumber,
        PreferredHospital: latest.preferredHospital,
        RelationshipToChild: latest.relationshipToChild,
        Name: latest.name,
        AuthEffectiveFrom: parseInt(latest.authEffectiveFrom) || 0,
        DayOf: latest.dayOf,
        YEAR: parseInt(latest.year) || 0,
        IsCopySign: !!copySignature,
        Signature: rawSig,
      };

      // Step 1: save form data
      const result = await saveMinorConsent(payload);
      if (result?.HasException) {
        toast.error(`Error saving Minor Consent. ${result.MessageText || ""}`);
        return false;
      }

      // Step 2: sync signature via ParentSign — awaited, error surfaced to user
      if (rawSig) {
        try {
          const sigResult = await saveParentSignatue({
            companyId,
            caseId,
            caseNo,
            userId: SessionData?.ID,
            formName: "MinorParent",
            signature: rawSig,
          });
          if (sigResult?.HasException) {
            toast.error("Form saved but signature sync failed.");
          }
        } catch (sigErr) {
          console.error("Signature sync error:", sigErr);
          toast.error("Form saved but signature sync failed.");
        }
      }

      toast.success("Minor Consent saved successfully.");
      return true;
    } catch (err) {
      console.error("saveFunction error:", err);
      toast.error("Error saving Minor Consent data.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  /* ── signature handlers ──────────────────────────────────────────────── */
  // Only store locally + persist to sessionStorage for copy-sign.
  // No API call here — save happens in saveFunction (same as EPAT).
  const signUpdatedCall = (base64Img) => {
    const newBase = base64Img.replace(
      /^data:image\/\w+;base64,/,
      "data:image/png;base64,",
    );
    setPatientSignImg(newBase);
    sessionStorage.setItem(SHARED_SIG_KEY, newBase); // persist for copy-sign
  };

  const clearSignCall = () => {
    setPatientSignImg("");
    signatureMiss?.(true);
  };

  /* ── provider field ──────────────────────────────────────────────────── */
  const ProviderField = () =>
    providerOptions.length === 1 ? (
      <input
        className="mcf__inline-input mcf__inline-input--lg"
        type="text"
        value={providerOptions[0]?.label || ""}
        readOnly
      />
    ) : (
      <div className="mcf__inline-select">
        <Select
          options={providerOptions}
          value={selectedProvider}
          onChange={(opt) => {
            setSelectedProvider(opt);
            setProviderName(opt?.label || "");
          }}
          classNamePrefix="react-select"
          placeholder="Select Provider"
          menuPortalTarget={document.body}
          styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
        />
      </div>
    );

  /* ── render ──────────────────────────────────────────────────────────── */
  if (loading) return <SpinnerLoader message="Loading form data…" />;

  return (
    <div className="mcf__wrapper">
      {/* ── Title ── */}
      <div className="mcf__title-block">
        <div className="mcf__title-main">Minor Consent Form</div>
      </div>

      {/* ── Authorization paragraph ── */}
      <div className="mcf__auth-para">
        <span className="mcf__auth-text">
          <b>I, HEREBY AUTHORIZE</b>
        </span>

        {isFrontDesk ? (
          <ProviderField />
        ) : (
          <input
            className="mcf__inline-input mcf__inline-input--lg"
            type="text"
            value={providerName}
            readOnly
          />
        )}

        <span className="mcf__auth-text">
          <b>
            AND WHOMEVER THE FACILITY DESIGNATES TO ADMINISTER ANY TESTING
            TREATMENTS AS DEEMED NECESSARY TO MY
          </b>
        </span>

        <input
          className="mcf__inline-input mcf__inline-input--md"
          type="text"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
          placeholder="Relationship"
        />

        <span className="mcf__auth-text">
          <b>AUTHORIZATION IS EFFECTIVE FROM THE</b>
        </span>

        <input
          className="mcf__inline-input mcf__inline-input--xs"
          type="text"
          value={authEffectiveFrom}
          onChange={(e) => setAuthEffectiveFrom(e.target.value)}
          placeholder="Day"
          maxLength={2}
        />

        <span className="mcf__auth-text">
          <b>Day Of</b>
        </span>

        <input
          className="mcf__inline-input mcf__inline-input--xs"
          type="text"
          value={dayOf}
          onChange={(e) => setDayOf(e.target.value)}
          placeholder="Mon"
          maxLength={3}
        />

        <span className="mcf__auth-text">
          <b>20</b>
        </span>

        <input
          className="mcf__inline-input mcf__inline-input--xs"
          type="text"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          placeholder="YY"
          maxLength={2}
        />

        <span className="mcf__auth-text">
          <b>UNTIL THE DURATION OF MINOR'S TREATMENT PERTAINING TO INCIDENT.</b>
        </span>
      </div>

      {/* ══════════════════════════════════════════
          Emergency Information
      ══════════════════════════════════════════ */}
      <div className="mcf__section">
        <span className="mcf__section-title">Emergency Information</span>
        <span className="mcf__section-line" />
      </div>

      <div className="mcf__grid-4">
        <div className="mcf__field mcf__field--span2">
          <label className="mcf__label">
            <b>Family Address</b>
          </label>
          <input
            className="mcf__input"
            type="text"
            value={familyAddress}
            onChange={(e) => setFamilyAddress(e.target.value)}
          />
        </div>

        <div className="mcf__field">
          <label className="mcf__label">
            <b>Father's Phone</b>
          </label>
          <input
            className="mcf__input"
            type="tel"
            value={fatherTelephone}
            onChange={(e) => setFatherTelephone(e.target.value)}
            placeholder="(999) 999-9999"
          />
        </div>

        <div className="mcf__field">
          <label className="mcf__label">
            <b>Mother's Phone</b>
          </label>
          <input
            className="mcf__input"
            type="tel"
            value={motherTelephone}
            onChange={(e) => setMotherTelephone(e.target.value)}
            placeholder="(999) 999-9999"
          />
        </div>

        <div className="mcf__field mcf__field--span2">
          <label className="mcf__label">
            <b>Insurance</b>
          </label>
          <Select
            options={insuranceList}
            value={insuranceId}
            classNamePrefix="react-select"
            placeholder="Select Insurance"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
            isDisabled
          />
        </div>

        <div className="mcf__field">
          <label className="mcf__label">
            <b>Policy #</b>
          </label>
          <input
            className="mcf__input"
            type="text"
            value={policy}
            onChange={(e) => setPolicy(e.target.value)}
          />
        </div>

        <div className="mcf__field">
          <label className="mcf__label">
            <b>Preferred Hospital</b>
          </label>
          <input
            className="mcf__input"
            type="text"
            value={preferredHospital}
            onChange={(e) => setPreferredHospital(e.target.value)}
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════
          Contact & Guardian Details
      ══════════════════════════════════════════ */}
      <div className="mcf__section" style={{ marginTop: 18 }}>
        <span className="mcf__section-title">Contact & Guardian Details</span>
        <span className="mcf__section-line" />
      </div>

      <div className="mcf__grid-4">
        <div className="mcf__field mcf__field--span2">
          <label className="mcf__label">
            <b>Emergency Contact Number</b>
          </label>
          <input
            className="mcf__input"
            type="tel"
            value={additionalContactNumber}
            onChange={(e) => setAdditionalContactNumber(e.target.value)}
            placeholder="(999) 999-9999"
          />
        </div>

        <div className="mcf__field mcf__field--span2">
          <label className="mcf__label">
            <b>Name</b>
          </label>
          <input
            className="mcf__input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="mcf__field mcf__field--span4">
          <label className="mcf__label">
            <b>Relationship To Child</b>
          </label>
          <input
            className="mcf__input"
            type="text"
            value={relationshipToChild}
            onChange={(e) => setRelationshipToChild(e.target.value)}
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════
          Signature
      ══════════════════════════════════════════ */}
      <div className="mcf__section" style={{ marginTop: 18 }}>
        <span className="mcf__section-title">
          Signature of Patient / Guardian
        </span>
        <span className="mcf__section-line" />
      </div>

      <div className="mcf__sig-row">
        <div
          className={`mcf__sign-box${!patientSignImg ? " mcf__sign-box--empty" : ""}`}
          onClick={() => setSigPadOpen(true)}
        >
          {patientSignImg ? (
            <img
              src={patientSignImg}
              alt="Patient signature"
              className="mcf__sign-img"
            />
          ) : (
            <span className="mcf__sign-hint">Click to sign</span>
          )}
        </div>
      </div>

      <SignaturePad
        isOpen={sigPadOpen}
        title="Patient / Guardian Signature"
        existingSign={patientSignImg}
        onClose={() => setSigPadOpen(false)}
        onSave={(base64) => {
          signUpdatedCall(base64);
          setSigPadOpen(false);
        }}
        onClear={clearSignCall}
      />
    </div>
  );
});

export default MinorConsentForm;


